import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Loader2, CheckCircle, XCircle, AlertTriangle, MessageSquare, Copy, RefreshCw, Sparkles, Mail, Target } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MatchDetail() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingMessage, setGeneratingMessage] = useState(false);
  const [tone, setTone] = useState("professional");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userRes = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" });
        const matchRes = await fetch(`${BACKEND_URL}/api/matches/${matchId}`, { credentials: "include" });
        if (userRes.ok) setUser(await userRes.json());
        if (matchRes.ok) setMatch(await matchRes.json());
      } catch (error) {
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [matchId]);

  const generateMessage = async () => {
    setGeneratingMessage(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/matches/${matchId}/generate-message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ match_id: matchId, tone })
      });
      if (!response.ok) throw new Error("Failed to generate");
      const data = await response.json();
      setMatch(prev => ({ ...prev, personalized_message: data.message }));
      toast.success("Message generated!");
    } catch (error) {
      toast.error("Failed to generate");
    } finally {
      setGeneratingMessage(false);
    }
  };

  const copyMessage = () => {
    if (match?.personalized_message) {
      navigator.clipboard.writeText(match.personalized_message);
      toast.success("Copied!");
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /></div>;
  }

  if (!match) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar user={user} />
        <main className="max-w-7xl mx-auto px-4 py-12">
          <Card className="p-12 text-center">
            <p className="text-slate-500">Match not found</p>
            <Button onClick={() => navigate("/dashboard")} className="mt-4">Back</Button>
          </Card>
        </main>
      </div>
    );
  }

  const investor = match.investor || {};
  const deck = match.deck || {};
  const pros = match.pros || [];
  const cons = match.cons || [];
  const conflicts = match.conflicts || [];
  const focusAreas = investor.focus_areas || [];
  const scoreColor = match.match_score >= 70 ? "bg-teal-50 border-teal-200 text-teal-600" : match.match_score >= 50 ? "bg-amber-50 border-amber-200 text-amber-600" : "bg-red-50 border-red-200 text-red-600";

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar user={user} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/analysis/${match.deck_id}`)} className="rounded-full" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">Match Details</h1>
            <p className="text-slate-500">{deck.filename} → {investor.name}</p>
          </div>
          <div className={`px-6 py-3 rounded-2xl border ${scoreColor}`}>
            <p className="text-xs text-center mb-1">Match Score</p>
            <p className="text-3xl font-bold font-mono">{match.match_score}%</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card className="border border-slate-200">
              <CardHeader><CardTitle className="flex items-center gap-2"><Target className="w-5 h-5 text-teal-600" />Match Rationale</CardTitle></CardHeader>
              <CardContent><p className="text-slate-700 text-lg">{match.rationale}</p></CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border border-teal-200">
                <CardHeader className="bg-teal-50/50"><CardTitle className="flex items-center gap-2 text-teal-700"><CheckCircle className="w-5 h-5" />Good Fit</CardTitle></CardHeader>
                <CardContent className="pt-4">
                  {pros.length > 0 ? (
                    <ul className="space-y-2">{pros.map((p, i) => <li key={i} className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-teal-500 mt-1" /><span>{p}</span></li>)}</ul>
                  ) : <p className="text-slate-500 text-sm">No pros identified</p>}
                </CardContent>
              </Card>

              <Card className="border border-amber-200">
                <CardHeader className="bg-amber-50/50"><CardTitle className="flex items-center gap-2 text-amber-700"><XCircle className="w-5 h-5" />Concerns</CardTitle></CardHeader>
                <CardContent className="pt-4">
                  {cons.length > 0 ? (
                    <ul className="space-y-2">{cons.map((c, i) => <li key={i} className="flex items-start gap-2"><XCircle className="w-4 h-4 text-amber-500 mt-1" /><span>{c}</span></li>)}</ul>
                  ) : <p className="text-slate-500 text-sm">No concerns</p>}
                </CardContent>
              </Card>
            </div>

            {conflicts.length > 0 && (
              <Card className="border border-red-200">
                <CardHeader className="bg-red-50/50"><CardTitle className="flex items-center gap-2 text-red-700"><AlertTriangle className="w-5 h-5" />Portfolio Conflicts</CardTitle></CardHeader>
                <CardContent className="pt-4">
                  <p className="text-sm text-slate-600 mb-3">Potential competitors:</p>
                  <div className="flex flex-wrap gap-2">{conflicts.map((c, i) => <Badge key={i} className="bg-red-50 text-red-700">{c}</Badge>)}</div>
                </CardContent>
              </Card>
            )}

            <Card className="border border-slate-200">
              <CardHeader><CardTitle className="flex items-center gap-2"><MessageSquare className="w-5 h-5" />Personalized Outreach</CardTitle></CardHeader>
              <CardContent>
                {!match.personalized_message ? (
                  <div className="text-center py-8">
                    <Sparkles className="w-12 h-12 text-teal-500 mx-auto mb-4" />
                    <h3 className="font-semibold mb-2">Generate Custom Email</h3>
                    <p className="text-slate-500 mb-6">Create a personalized message</p>
                    <div className="flex items-center justify-center gap-4">
                      <Select value={tone} onValueChange={setTone}>
                        <SelectTrigger className="w-40" data-testid="tone-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="professional">Professional</SelectItem>
                          <SelectItem value="friendly">Friendly</SelectItem>
                          <SelectItem value="concise">Concise</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button onClick={generateMessage} disabled={generatingMessage} className="btn-secondary" data-testid="generate-message-btn">
                        {generatingMessage ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}Generate
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-slate-50 rounded-xl p-6"><pre className="whitespace-pre-wrap font-sans text-slate-700 text-sm">{match.personalized_message}</pre></div>
                    <div className="flex justify-between">
                      <Button variant="outline" onClick={generateMessage} disabled={generatingMessage} className="rounded-full">
                        {generatingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}Regenerate
                      </Button>
                      <Button onClick={copyMessage} className="btn-primary" data-testid="copy-message-btn"><Copy className="w-4 h-4 mr-2" />Copy</Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="border border-slate-200 sticky top-24">
              <CardHeader className="bg-slate-50">
                <CardTitle className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center"><span className="text-white font-bold">{investor.name?.charAt(0)}</span></div>
                  <div><p className="font-semibold">{investor.name}</p><p className="text-sm text-slate-500 font-normal">{investor.firm}</p></div>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                {investor.typical_check_size && <div className="flex items-center gap-2 text-sm"><span className="text-slate-500">Check Size:</span><span className="font-medium">{investor.typical_check_size}</span></div>}
                {focusAreas.length > 0 && (
                  <div><p className="text-sm text-slate-500 mb-2">Focus Areas</p><div className="flex flex-wrap gap-1">{focusAreas.slice(0, 5).map((a, i) => <Badge key={i} variant="secondary" className="text-xs">{a}</Badge>)}</div></div>
                )}
                <Button variant="outline" className="w-full rounded-full mt-4" onClick={() => navigate(`/investors/${investor.investor_id}`)} data-testid="view-investor-btn">View Profile</Button>
                {investor.email && <a href={`mailto:${investor.email}`} className="block"><Button className="w-full btn-secondary"><Mail className="w-4 h-4 mr-2" />Contact</Button></a>}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
